import {
  mailAccentPreset,
  readMailAccent,
  type MailAccentId,
} from "@/lib/theme/mailAccent";

export type MailTheme = "light" | "dark";

export const MAIL_THEME_STORAGE_KEY = "mail_theme";

const MAIL_PWA_THEME_COLOR: Record<MailTheme, string> = {
  light: "#f8faff",
  dark: "#131c2e",
};

export function readMailTheme(): MailTheme {
  try {
    const stored = localStorage.getItem(MAIL_THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    /* ignore */
  }
  return "light";
}

export function persistMailTheme(theme: MailTheme): void {
  try {
    localStorage.setItem(MAIL_THEME_STORAGE_KEY, theme);
  } catch {
    /* ignore */
  }
}

function applyMailAccentVars(theme: MailTheme, accentId: MailAccentId): void {
  if (typeof document === "undefined") return;
  const preset = mailAccentPreset(accentId);
  const root = document.documentElement;

  root.dataset.mailAccent = preset.id;

  const style = root.style;
  style.setProperty("--mail-accent", preset.accent);
  style.setProperty("--mail-accent-rgb", preset.accentRgb);
  style.setProperty("--mail-accent-strong", preset.accentStrong);
  style.setProperty("--mail-accent-dark", preset.accentStrong);
  style.setProperty("--mail-accent-gradient-from", preset.gradientFrom);
  style.setProperty("--mail-accent-gradient-to", preset.gradientTo);
  style.setProperty("--mail-ui-icon", theme === "dark" ? preset.uiIconDark : preset.uiIcon);
  style.setProperty(
    "--mail-accent-soft",
    theme === "dark"
      ? `rgb(${preset.accentRgb} / 0.14)`
      : `color-mix(in srgb, ${preset.accent} 12%, #ffffff)`
  );
  style.setProperty("--mail-primary", preset.accentStrong);
  style.setProperty("--mail-primary-hover", preset.accent);
  style.setProperty(
    "--mail-primary-gradient",
    `linear-gradient(90deg, ${preset.gradientFrom} 0%, ${preset.accent} 50%, ${preset.gradientTo} 100%)`
  );
  style.setProperty(
    "--mail-primary-gradient-hover",
    `linear-gradient(90deg, ${preset.accentStrong} 0%, ${preset.accent} 50%, ${preset.gradientTo} 100%)`
  );
}

export function applyMailTheme(theme: MailTheme, accent: MailAccentId = readMailAccent()): void {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.theme = theme;
  applyMailAccentVars(theme, accent);

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta instanceof HTMLMetaElement) {
    meta.content = MAIL_PWA_THEME_COLOR[theme];
  }
}

export function applyMailAccent(accentId: MailAccentId, theme: MailTheme = readMailTheme()): void {
  applyMailTheme(theme, accentId);
}

export function toggleMailTheme(current: MailTheme): MailTheme {
  return current === "dark" ? "light" : "dark";
}
